import { useEffect, useMemo, useRef, useState } from 'react';
import { controlDefinitions, getControlDefinition } from './data/controlDefinitions';
import { eventLevels, eventTypes, proactiveSource, relatedCategories, sifDefinition } from './data/proactive';
import {
  deleteDraft,
  deleteHistory,
  loadDrafts,
  loadHistory,
  loadProfile,
  saveDraft,
  saveHistory,
  saveProfile,
} from './storage';
import type {
  ControlDefinition,
  ControlInstance,
  CriticalSession,
  DraftRecord,
  ItemResponse,
  Participant,
  PhotoRecord,
  ProactiveReport,
  Profile,
  ResponseState,
  SharedDetails,
} from './types';
import {
  activeResponses,
  buildCriticalEmailBody,
  buildCriticalReportHtml,
  buildProactiveEmailBody,
  buildProactiveReportHtml,
  createPhoto,
  currentTime,
  downloadHtml,
  id,
  photoCountLabel,
  photosForResponse,
  photoTotal,
  prepareEmail,
  responseLabel,
  submissionReportFile,
  today,
  validateCriticalSession,
  validateProactive,
} from './utils';

type ThemePreference = 'light' | 'dark';

type View =
  | 'home'
  | 'setup'
  | 'critical_select'
  | 'critical_session'
  | 'critical_work'
  | 'critical_review'
  | 'proactive'
  | 'proactive_review'
  | 'history'
  | 'view_record';

type Issue = { key: string; label: string; message: string };
type DeleteTarget = { record: DraftRecord; fromHistory: boolean };

const blankProfile: Profile = {
  id: 'local_profile',
  name: '',
  pcBranch: '',
  defaultRecipients: [''],
  updatedAt: new Date().toISOString(),
};

const createSharedDetails = (profile?: Profile): SharedDetails => ({
  date: today(),
  time: currentTime(),
  observer: profile?.name || '',
  pcBranch: profile?.pcBranch || '',
  employeeNumber: profile?.employeeNumber || '',
  additionalParticipants: [],
});

const createResponses = (definition: ControlDefinition): ItemResponse[] =>
  definition.sections.flatMap((section) =>
    section.items.map((item) => ({
      itemId: item.id,
      questionSnapshot: { ...item },
      state: undefined,
      comment: '',
      flagged: false,
      photoIds: [],
    })),
  );

const cloneDefinition = (definition: ControlDefinition): ControlDefinition =>
  JSON.parse(JSON.stringify(definition)) as ControlDefinition;

const createControlInstance = (definition: ControlDefinition, shared: SharedDetails): ControlInstance => ({
  id: id('control'),
  definitionId: definition.id,
  definitionSnapshot: cloneDefinition(definition),
  editableDetails: {
    date: shared.date,
    time: shared.time,
    observer: shared.observer,
    pcBranch: shared.pcBranch,
    employeeNumber: shared.employeeNumber || '',
    ...Object.fromEntries(definition.headerFields.map((field) => [field.id, field.id === 'observer' ? shared.observer : ''])),
  },
  responses: createResponses(definition),
  status: 'draft',
  createdAt: new Date().toISOString(),
});

const createCriticalSession = (profile: Profile | undefined, selectedControlIds: string[]): CriticalSession => {
  const shared = createSharedDetails(profile);
  return {
    id: id('critical'),
    type: 'critical',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    sharedDetails: shared,
    selectedControlIds,
    controlInstances: [],
    photos: [],
    recipients: profile?.defaultRecipients.filter(Boolean) || [],
    status: 'draft',
  };
};

const createProactive = (profile?: Profile): ProactiveReport => ({
  id: id('proactive'),
  type: 'proactive',
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
  fields: {
    pcNumber: profile?.pcBranch || '',
    date: today(),
    time: currentTime(),
    name: profile?.name || '',
    employeeNumber: profile?.employeeNumber || '',
    additionalParticipants: [],
    location: '',
    eventType: '',
    eventOther: '',
    eventLevel: '',
    related: [],
    relatedOther: '',
    observation: '',
    correctiveAction: '',
  },
  photos: [],
  recipients: profile?.defaultRecipients.filter(Boolean) || [],
  status: 'draft',
  sourceSnapshot: {
    title: proactiveSource.title,
    revision: proactiveSource.revision,
    eventLevels,
  },
});

export function App() {
  const [view, setView] = useState<View>('home');
  const [profile, setProfile] = useState<Profile | undefined>();
  const [profileForm, setProfileForm] = useState<Profile>(blankProfile);
  const [drafts, setDrafts] = useState<DraftRecord[]>([]);
  const [history, setHistory] = useState<DraftRecord[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [critical, setCritical] = useState<CriticalSession | undefined>();
  const [activeControlId, setActiveControlId] = useState<string>('');
  const [proactive, setProactive] = useState<ProactiveReport | undefined>();
  const [issues, setIssues] = useState<Issue[]>([]);
  const [online, setOnline] = useState(navigator.onLine);
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);
  const [viewRecord, setViewRecord] = useState<DraftRecord | undefined>();
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | undefined>();
  const [theme, setTheme] = useState<ThemePreference>(() => {
    const saved = localStorage.getItem('critical_control_theme');
    return saved === 'dark' ? 'dark' : 'light';
  });
  const autosaveTimer = useRef<number | undefined>(undefined);

  useEffect(() => {
    void refreshLocalData();
    const onOnline = () => setOnline(true);
    const onOffline = () => setOnline(false);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, []);

  useEffect(() => {
    if (critical) scheduleDraftSave(critical);
  }, [critical]);

  useEffect(() => {
    if (proactive) scheduleDraftSave(proactive);
  }, [proactive]);

  useEffect(() => {
    localStorage.setItem('critical_control_theme', theme);
    document.documentElement.dataset.theme = theme;
  }, [theme]);

  useEffect(() => {
    if (!notice) return undefined;
    const timer = window.setTimeout(() => setNotice(''), 5000);
    return () => window.clearTimeout(timer);
  }, [notice]);

  const pendingCount = useMemo(
    () => [...drafts, ...history].filter((record) => record.status === 'pending_send').length,
    [drafts, history],
  );

  async function refreshLocalData() {
    const [loadedProfile, loadedDrafts, loadedHistory] = await Promise.all([loadProfile(), loadDrafts(), loadHistory()]);
    setProfile(loadedProfile);
    setProfileForm(loadedProfile || blankProfile);
    setDrafts(loadedDrafts.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt)));
    setHistory(loadedHistory.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt)));
    if (!loadedProfile) setView('setup');
  }

  function scheduleDraftSave(record: DraftRecord) {
    window.clearTimeout(autosaveTimer.current);
    autosaveTimer.current = window.setTimeout(async () => {
      if (record.status === 'draft' || record.status === 'pending_send') {
        await saveDraft({ ...record, updatedAt: new Date().toISOString() });
        const loadedDrafts = await loadDrafts();
        setDrafts(loadedDrafts.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt)));
      }
    }, 450);
  }

  async function persistProfile() {
    const cleanRecipients = profileForm.defaultRecipients.map((item) => item.trim()).filter(Boolean);
    if (!profileForm.name.trim() || !profileForm.pcBranch.trim() || cleanRecipients.length === 0) {
      setIssues([{ key: 'profile', label: 'Setup', message: 'Name, PC / Branch number, and at least one recipient are required.' }]);
      return;
    }
    const next = { ...profileForm, defaultRecipients: cleanRecipients, updatedAt: new Date().toISOString() };
    await saveProfile(next);
    setProfile(next);
    setIssues([]);
    setNotice('Setup saved locally.');
    setView('home');
    await refreshLocalData();
  }

  function startCritical() {
    setSelectedIds([]);
    setIssues([]);
    setView('critical_select');
  }

  function continueToSession() {
    if (!selectedIds.length) {
      setIssues([{ key: 'selection', label: 'Critical Controls', message: 'Select at least one Critical Control.' }]);
      return;
    }
    const session = createCriticalSession(profile, selectedIds);
    setCritical(session);
    setIssues([]);
    setView('critical_session');
  }

  function createControlsFromShared() {
    if (!critical) return;
    const shared = critical.sharedDetails;
    const missing: Issue[] = [];
    if (!shared.observer.trim()) missing.push({ key: 'observer', label: 'Session details', message: 'Name is required.' });
    if (!shared.pcBranch.trim()) missing.push({ key: 'pcBranch', label: 'Session details', message: 'PC / Branch number is required.' });
    (shared.additionalParticipants || []).forEach((participant, index) => {
      if (!participant.name.trim()) {
        missing.push({
          key: `participant_${participant.id}_name`,
          label: 'Session details',
          message: `Participant ${index + 1} name is required.`,
        });
      }
    });
    if (missing.length) {
      setIssues(missing);
      return;
    }
    const instances = critical.selectedControlIds
      .map(getControlDefinition)
      .filter(Boolean)
      .map((definition) => createControlInstance(definition as ControlDefinition, shared));
    setCritical({ ...critical, controlInstances: instances, updatedAt: new Date().toISOString() });
    setActiveControlId(instances[0]?.id || '');
    setIssues([]);
    setView('critical_work');
  }

  function updateCritical(updater: (session: CriticalSession) => CriticalSession) {
    setCritical((current) => (current ? updater(current) : current));
  }

  function updateResponse(controlId: string, itemId: string, patch: Partial<ItemResponse>) {
    updateCritical((session) => ({
      ...session,
      updatedAt: new Date().toISOString(),
      controlInstances: session.controlInstances.map((control) =>
        control.id === controlId
          ? {
              ...control,
              responses: control.responses.map((response) =>
                response.itemId === itemId ? { ...response, ...patch } : response,
              ),
            }
          : control,
      ),
    }));
  }

  function resetControlToShared(controlId: string) {
    updateCritical((session) => ({
      ...session,
      controlInstances: session.controlInstances.map((control) =>
        control.id === controlId
          ? {
              ...control,
              editableDetails: {
                ...control.editableDetails,
                date: session.sharedDetails.date,
                time: session.sharedDetails.time,
                observer: session.sharedDetails.observer,
                pcBranch: session.sharedDetails.pcBranch,
                employeeNumber: session.sharedDetails.employeeNumber || '',
              },
            }
          : control,
      ),
    }));
  }

  function goToIssue(issue: Issue) {
    if (critical) {
      const control = critical.controlInstances.find((candidate) =>
        candidate.responses.some((response) => response.itemId === issue.key),
      );
      if (control) {
        setActiveControlId(control.id);
        setView('critical_work');
      } else if (issue.key === 'observer' || issue.key === 'pcBranch' || issue.key.startsWith('participant_')) {
        setView('critical_session');
      }
    }
    if (proactive) {
      setView('proactive');
    }
    window.setTimeout(() => {
      const target = document.getElementById(issue.key);
      target?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      target?.classList.add('attention_flash');
    }, 80);
  }

  function validateCriticalReview() {
    if (!critical) return;
    const nextIssues = validateCriticalSession(critical);
    setIssues(nextIssues);
    if (!nextIssues.length) {
      setCritical({ ...critical, reportHtml: buildCriticalReportHtml(critical), updatedAt: new Date().toISOString() });
      setView('critical_review');
    } else {
      window.setTimeout(() => {
        document.getElementById('critical_validation_near_action')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 80);
    }
  }

  async function attachCriticalPhoto(file: File, controlId?: string, itemId?: string) {
    if (!critical || critical.photos.length >= 3) return;
    setBusy(true);
    try {
      const photo = await createPhoto(file, itemId ? 'control_item' : 'general', itemId, undefined, controlId);
      updateCritical((session) => ({
        ...session,
        photos: [...session.photos, photo],
        controlInstances: itemId
          ? session.controlInstances.map((control) => ({
              ...control,
              responses: control.responses.map((response) =>
                control.id === controlId && response.itemId === itemId ? { ...response, photoIds: [...response.photoIds, photo.id] } : response,
              ),
            }))
          : session.controlInstances,
      }));
    } catch (error) {
      setNotice(error instanceof Error ? error.message : 'Photo processing failed.');
    } finally {
      setBusy(false);
    }
  }

  function removeCriticalPhoto(photoId: string) {
    updateCritical((session) => ({
      ...session,
      photos: session.photos.filter((photo) => photo.id !== photoId),
      controlInstances: session.controlInstances.map((control) => ({
        ...control,
        responses: control.responses.map((response) => ({
          ...response,
          photoIds: response.photoIds.filter((idValue) => idValue !== photoId),
        })),
      })),
    }));
  }

  function startProactive() {
    setProactive(createProactive(profile));
    setIssues([]);
    setView('proactive');
  }

  function validateProactiveReview() {
    if (!proactive) return;
    const nextIssues = validateProactive(proactive);
    setIssues(nextIssues);
    if (!nextIssues.length) {
      setProactive({ ...proactive, reportHtml: buildProactiveReportHtml(proactive), updatedAt: new Date().toISOString() });
      setView('proactive_review');
    } else {
      window.setTimeout(() => {
        document.getElementById('proactive_validation_near_action')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 80);
    }
  }

  async function attachProactivePhoto(file: File, label?: string) {
    if (!proactive || proactive.photos.length >= 3) return;
    setBusy(true);
    try {
      const photo = await createPhoto(file, 'proactive', undefined, label);
      setProactive({ ...proactive, photos: [...proactive.photos, photo], updatedAt: new Date().toISOString() });
    } catch (error) {
      setNotice(error instanceof Error ? error.message : 'Photo processing failed.');
    } finally {
      setBusy(false);
    }
  }

  async function completeRecord(record: DraftRecord, mode: 'local' | 'export' | 'email') {
    const reportHtml = record.type === 'critical' ? buildCriticalReportHtml(record) : buildProactiveReportHtml(record);
    let status = mode === 'email' ? (online ? 'prepared_email' : 'pending_send') : mode === 'export' ? 'exported' : 'completed_local';
    let completed = { ...record, status, reportHtml, updatedAt: new Date().toISOString() } as DraftRecord;
    let emailNotice = '';
    if (mode === 'email') {
      if (online) {
        try {
          const subject = completed.type === 'critical' ? 'Critical Control Report' : 'Proactive Event Report';
          const body = completed.type === 'critical' ? buildCriticalEmailBody(completed) : buildProactiveEmailBody(completed);
          await prepareEmail(subject, body, completed.recipients, await submissionReportFile(completed), completed.photos);
          emailNotice = 'Report emailed successfully with the complete structured body and PDF attachment.';
        } catch (error) {
          status = 'pending_send';
          completed = { ...completed, status, updatedAt: new Date().toISOString() } as DraftRecord;
          emailNotice = error instanceof Error ? error.message : 'Automatic email failed. The report was saved locally and was not sent.';
        }
      } else {
        emailNotice = 'Report completed locally and marked Pending Send. Open History when online to send it.';
      }
    }
    await saveHistory(completed);
    await deleteDraft(record.id);
    if (mode === 'export') downloadHtml(reportHtml, `${record.type}_${record.id}.html`);
    if (emailNotice) setNotice(emailNotice);
    setCritical(undefined);
    setProactive(undefined);
    await refreshLocalData();
    setView('history');
  }

  function resumeRecord(record: DraftRecord) {
    if (record.type === 'critical') {
      setCritical(record);
      setActiveControlId(record.controlInstances[0]?.id || '');
      setView(record.controlInstances.length ? 'critical_work' : 'critical_session');
    } else {
      setProactive(record);
      setView('proactive');
    }
  }

  function openRecord(record: DraftRecord) {
    setViewRecord(record);
    setView('view_record');
  }

  function requestDelete(record: DraftRecord, fromHistory = false) {
    setDeleteTarget({ record, fromHistory });
  }

  async function confirmDelete() {
    if (!deleteTarget) return;
    if (deleteTarget.fromHistory) await deleteHistory(deleteTarget.record.id);
    else await deleteDraft(deleteTarget.record.id);
    setDeleteTarget(undefined);
    await refreshLocalData();
  }

  const activeControl = critical?.controlInstances.find((control) => control.id === activeControlId);

  return (
    <div className="app_shell">
      <header className="topbar">
        <button className="brand_button" onClick={() => setView('home')} aria-label="Go home">
          <span className="brand_mark">CC</span>
          <span>
            <strong>Critical Control Hub</strong>
            <small>{online ? 'Online' : 'Offline'}</small>
          </span>
        </button>
        <nav aria-label="Primary navigation">
          <button onClick={() => setView('history')}>History</button>
          <button onClick={() => setView('setup')}>Setup</button>
        </nav>
      </header>

      {pendingCount > 0 && (
        <button className="pending_banner" onClick={() => setView('history')}>
          {pendingCount} report{pendingCount === 1 ? '' : 's'} pending send
        </button>
      )}

      <main>
        {notice && (
          <div className="notice" role="status">
            {notice}
            <button onClick={() => setNotice('')} aria-label="Dismiss notice">Dismiss</button>
          </div>
        )}
        {issues.length > 0 && view !== 'critical_work' && <ValidationSummary issues={issues} onGo={goToIssue} />}

        {view === 'home' && <Home profile={profile} drafts={drafts} onCritical={startCritical} onProactive={startProactive} onResume={resumeRecord} />}
        {view === 'setup' && <Setup profileForm={profileForm} setProfileForm={setProfileForm} theme={theme} setTheme={setTheme} onSave={persistProfile} />}
        {view === 'critical_select' && <CriticalSelect selectedIds={selectedIds} setSelectedIds={setSelectedIds} onContinue={continueToSession} />}
        {view === 'critical_session' && critical && (
          <SessionDetails
            session={critical}
            update={(shared) => updateCritical((session) => ({ ...session, sharedDetails: shared }))}
            onBack={() => setView('critical_select')}
            onCreate={createControlsFromShared}
          />
        )}
        {view === 'critical_work' && critical && activeControl && (
          <CriticalWork
            session={critical}
            activeControl={activeControl}
            setActiveControlId={setActiveControlId}
            updateControl={(control) =>
              updateCritical((session) => ({
                ...session,
                controlInstances: session.controlInstances.map((item) => (item.id === control.id ? control : item)),
              }))
            }
            updateResponse={updateResponse}
            resetControlToShared={resetControlToShared}
            attachPhoto={attachCriticalPhoto}
            removePhoto={removeCriticalPhoto}
            onReview={validateCriticalReview}
            issues={issues}
            onGoToIssue={goToIssue}
            busy={busy}
          />
        )}
        {view === 'critical_review' && critical && (
          <CriticalReview session={critical} setSession={setCritical} onBack={() => setView('critical_work')} onComplete={completeRecord} online={online} />
        )}
        {view === 'proactive' && proactive && (
          <ProactiveForm
            report={proactive}
            setReport={setProactive}
            attachPhoto={attachProactivePhoto}
            removePhoto={(photoId) => setProactive({ ...proactive, photos: proactive.photos.filter((photo) => photo.id !== photoId) })}
            onReview={validateProactiveReview}
            issues={issues}
            onGoToIssue={goToIssue}
            busy={busy}
          />
        )}
        {view === 'proactive_review' && proactive && (
          <ProactiveReview report={proactive} setReport={setProactive} onBack={() => setView('proactive')} onComplete={completeRecord} online={online} />
        )}
        {view === 'history' && <History drafts={drafts} history={history} onHome={() => setView('home')} onResume={resumeRecord} onView={openRecord} onDelete={requestDelete} onPrepare={completeRecord} />}
        {view === 'view_record' && viewRecord && (
          <ReadOnlyRecord record={viewRecord} onBack={() => setView('history')} onPrepare={completeRecord} online={online} />
        )}
      </main>
      {deleteTarget && <DeleteDialog target={deleteTarget} onCancel={() => setDeleteTarget(undefined)} onConfirm={confirmDelete} />}
    </div>
  );
}

function Home({ profile, drafts, onCritical, onProactive, onResume }: { profile?: Profile; drafts: DraftRecord[]; onCritical: () => void; onProactive: () => void; onResume: (record: DraftRecord) => void }) {
  return (
    <section className="home_grid">
      <div className="panel intro_panel">
        <h1>Critical Control Hub</h1>
        <p>Local first Critical Control checklists and Proactive reports.</p>
        <div className="action_grid">
          <button className="primary_action" onClick={onCritical}>Critical Controls</button>
          <button className="primary_action" onClick={onProactive}>Proactive</button>
        </div>
        <a className="take10_link" href="https://take10.work" target="_blank" rel="noreferrer">Need to complete a Take 10?</a>
      </div>
      <div className="panel">
        <h2>Profile</h2>
        {profile ? (
          <dl className="meta_list">
            <div><dt>Name</dt><dd>{profile.name}</dd></div>
            <div><dt>PC / Branch</dt><dd>{profile.pcBranch}</dd></div>
            <div><dt>Recipients</dt><dd>{profile.defaultRecipients.join(', ')}</dd></div>
          </dl>
        ) : <p>Setup is required before final send or export.</p>}
      </div>
      <div className="panel wide_panel">
        <h2>Resume Draft</h2>
        {drafts.length ? (
          <div className="record_list">
            {drafts.slice(0, 4).map((record) => <RecordRow key={record.id} record={record} actionLabel="Resume" onAction={() => onResume(record)} />)}
          </div>
        ) : <p>No saved drafts.</p>}
      </div>
    </section>
  );
}

function Setup({ profileForm, setProfileForm, theme, setTheme, onSave }: { profileForm: Profile; setProfileForm: (profile: Profile) => void; theme: ThemePreference; setTheme: (theme: ThemePreference) => void; onSave: () => void }) {
  const recipients = profileForm.defaultRecipients.length ? profileForm.defaultRecipients : [''];
  return (
    <section className="panel form_panel">
      <h1>Setup</h1>
      <Field label="Employee / user name" required><input value={profileForm.name} onChange={(event) => setProfileForm({ ...profileForm, name: event.target.value })} /></Field>
      <Field label="PC / Branch number" required><input value={profileForm.pcBranch} onChange={(event) => setProfileForm({ ...profileForm, pcBranch: event.target.value })} /></Field>
      <Field label="Employee number"><input value={profileForm.employeeNumber || ''} onChange={(event) => setProfileForm({ ...profileForm, employeeNumber: event.target.value })} /></Field>
      <div className="field_group">
        <span className="field_label">Default email recipient(s) <b>Required</b></span>
        {recipients.map((recipient, index) => (
          <div className="recipient_row" key={index}>
            <input type="email" value={recipient} onChange={(event) => {
              const next = [...recipients];
              next[index] = event.target.value;
              setProfileForm({ ...profileForm, defaultRecipients: next });
            }} aria-label={`Recipient ${index + 1}`} />
            <button type="button" onClick={() => setProfileForm({ ...profileForm, defaultRecipients: recipients.filter((_, itemIndex) => itemIndex !== index) })}>Remove</button>
          </div>
        ))}
        <button type="button" onClick={() => setProfileForm({ ...profileForm, defaultRecipients: [...recipients, ''] })}>Add recipient</button>
      </div>
      <Field label="Appearance">
        <select value={theme} onChange={(event) => setTheme(event.target.value as ThemePreference)}>
          <option value="light">Light</option>
          <option value="dark">Dark</option>
        </select>
      </Field>
      <button className="primary_action compact" onClick={onSave}>Save Setup</button>
    </section>
  );
}

function CriticalSelect({ selectedIds, setSelectedIds, onContinue }: { selectedIds: string[]; setSelectedIds: (ids: string[]) => void; onContinue: () => void }) {
  return (
    <section className="panel">
      <div className="section_header">
        <div><h1>Critical Controls</h1><p>Select one or more controls for this session.</p></div>
        <div className="selection_actions"><strong>{selectedIds.length} selected</strong>{selectedIds.length > 0 && <button className="primary_action compact" onClick={onContinue}>Proceed ({selectedIds.length})</button>}</div>
      </div>
      <div className="control_select_grid">
        {controlDefinitions.map((control) => {
          const selected = selectedIds.includes(control.id);
          return (
            <button key={control.id} className={`select_card ${selected ? 'selected' : ''}`} onClick={() => setSelectedIds(selected ? selectedIds.filter((item) => item !== control.id) : [...selectedIds, control.id])} aria-pressed={selected}>
              <span>{control.code}</span>
              <strong>{control.title}</strong>
              <small>{control.revision} | {control.sourceDate}</small>
            </button>
          );
        })}
      </div>
      <button className="primary_action compact sticky_action" onClick={onContinue}>Continue ({selectedIds.length})</button>
    </section>
  );
}

function SessionDetails({ session, update, onBack, onCreate }: { session: CriticalSession; update: (shared: SharedDetails) => void; onBack: () => void; onCreate: () => void }) {
  const set = (key: keyof SharedDetails, value: string) => update({ ...session.sharedDetails, [key]: value });
  const participants = session.sharedDetails.additionalParticipants || [];
  const setParticipant = (participantId: string, patch: Partial<Participant>) => {
    update({
      ...session.sharedDetails,
      additionalParticipants: participants.map((participant) => participant.id === participantId ? { ...participant, ...patch } : participant),
    });
  };
  const addParticipant = () => update({ ...session.sharedDetails, additionalParticipants: [...participants, { id: id('participant'), name: '', employeeNumber: '' }] });
  const removeParticipant = (participantId: string) => update({ ...session.sharedDetails, additionalParticipants: participants.filter((participant) => participant.id !== participantId) });
  return (
    <section className="panel form_panel session_panel">
      <h1>Session Details</h1>
      <div className="two_col compact_fields">
        <Field label="Date" required><input type="date" value={session.sharedDetails.date} onChange={(e) => set('date', e.target.value)} /></Field>
        <Field label="Time" required><input type="time" value={session.sharedDetails.time} onChange={(e) => set('time', e.target.value)} /></Field>
      </div>
      <Field label="Name" required><input id="observer" value={session.sharedDetails.observer} onChange={(e) => set('observer', e.target.value)} /></Field>
      <div className="two_col compact_fields">
        <Field label="PC / Branch" required><input id="pcBranch" value={session.sharedDetails.pcBranch} onChange={(e) => set('pcBranch', e.target.value)} /></Field>
        <Field label="Employee number"><input value={session.sharedDetails.employeeNumber || ''} onChange={(e) => set('employeeNumber', e.target.value)} /></Field>
      </div>
      <div className="participants_box">
        <div className="section_header compact_header"><h2>Additional Participants</h2><button type="button" onClick={addParticipant}>Add participant</button></div>
        {participants.map((participant, index) => (
          <div className="participant_row" key={participant.id}>
            <Field label={`Participant ${index + 1} name`} required><input id={`participant_${participant.id}_name`} value={participant.name} onChange={(event) => setParticipant(participant.id, { name: event.target.value })} /></Field>
            <Field label="Employee number"><input value={participant.employeeNumber || ''} onChange={(event) => setParticipant(participant.id, { employeeNumber: event.target.value })} /></Field>
            <button type="button" onClick={() => removeParticipant(participant.id)}>Remove</button>
          </div>
        ))}
      </div>
      <div className="button_row"><button onClick={onBack}>Back</button><button className="primary_action compact" onClick={onCreate}>Create Controls</button></div>
    </section>
  );
}

const controlProgress = (control: ControlInstance) => {
  const responses = activeResponses(control.responses);
  const answered = responses.filter((response) => response.state).length;
  const exposedMissingComment = responses.filter((response) => response.state === 'exposed' && !String(response.comment || '').trim()).length;
  return { answered, total: responses.length, remaining: responses.length - answered, exposedMissingComment };
};

const controlReadyForReview = (control: ControlInstance) => {
  const progress = controlProgress(control);
  return progress.remaining === 0 && progress.exposedMissingComment === 0;
};

function CriticalWork({ session, activeControl, setActiveControlId, updateControl, updateResponse, resetControlToShared, attachPhoto, removePhoto, onReview, issues, onGoToIssue, busy }: { session: CriticalSession; activeControl: ControlInstance; setActiveControlId: (id: string) => void; updateControl: (control: ControlInstance) => void; updateResponse: (controlId: string, itemId: string, patch: Partial<ItemResponse>) => void; resetControlToShared: (controlId: string) => void; attachPhoto: (file: File, controlId?: string, itemId?: string) => void; removePhoto: (photoId: string) => void; onReview: () => void; issues: Issue[]; onGoToIssue: (issue: Issue) => void; busy: boolean }) {
  const { answered, total, remaining, exposedMissingComment } = controlProgress(activeControl);
  const nextIncomplete = session.controlInstances.find((control) => control.id !== activeControl.id && !controlReadyForReview(control));
  const allControlsReady = session.controlInstances.every(controlReadyForReview);
  return (
    <section className={`work_layout ${session.controlInstances.length === 1 ? 'single_control' : ''}`}>
      {session.controlInstances.length > 1 && (
        <aside className="panel control_nav">
          <h2>Controls</h2>
          {session.controlInstances.map((control) => {
            const active = control.id === activeControl.id;
            const progress = controlProgress(control);
            const ready = controlReadyForReview(control);
            return (
              <button className={active ? 'active' : ''} key={control.id} onClick={() => setActiveControlId(control.id)}>
                <span>{control.definitionSnapshot.code}</span>
                <strong>{control.definitionSnapshot.title}</strong>
                <small>{progress.answered} of {progress.total} {ready ? 'Complete' : 'Remaining'}</small>
              </button>
            );
          })}
        </aside>
      )}
      <div className="panel checklist_panel">
        <div className="section_header">
          <div><h1>{activeControl.definitionSnapshot.title}</h1><p>{activeControl.definitionSnapshot.instructions}</p><small>{activeControl.definitionSnapshot.revision} | {activeControl.definitionSnapshot.sourceDate}</small></div>
          <div className="counter_box"><strong>{answered} of {total}</strong><span>Items answered</span></div>
        </div>
        <div className="work_tools">
          <PhotoCount photos={session.photos} />
          <ControlDetails control={activeControl} setControl={updateControl} onReset={() => resetControlToShared(activeControl.id)} />
        </div>
        {activeControl.definitionSnapshot.sourceNotes?.map((note) => <p className="source_note" key={note}>{note}</p>)}
        {activeControl.definitionSnapshot.sections.map((section) => (
          <div className="checklist_section" key={section.id}>
            {section.title && <h2>{section.title}</h2>}
            <div className="checklist_header" aria-hidden="true">
              <span>#</span><span>Control Item</span><span>Response</span><span>Actions</span>
            </div>
            {section.items.map((item) => {
              const response = activeControl.responses.find((entry) => entry.itemId === item.id);
              if (!response) return null;
              return <ChecklistRow key={item.id} controlId={activeControl.id} response={response} photos={session.photos} atPhotoMax={session.photos.length >= 3} updateResponse={updateResponse} attachPhoto={attachPhoto} removePhoto={removePhoto} busy={busy} />;
            })}
          </div>
        ))}
        {issues.length > 0 && (
          <div id="critical_validation_near_action">
            <ValidationSummary issues={issues} onGo={onGoToIssue} />
          </div>
        )}
        <div className="workflow_actions" id="review_action_area">
          {!allControlsReady && nextIncomplete && controlReadyForReview(activeControl) && (
            <button className="primary_action compact" onClick={() => setActiveControlId(nextIncomplete.id)}>Next Critical Control: {nextIncomplete.definitionSnapshot.title}</button>
          )}
          {!allControlsReady && !controlReadyForReview(activeControl) && (
            <div className="remaining_hint">
              <strong>{remaining + exposedMissingComment} item{remaining + exposedMissingComment === 1 ? '' : 's'} remaining in this control</strong>
              <span>Use Review to see exact missing items when needed.</span>
            </div>
          )}
          {allControlsReady ? <button className="primary_action compact" onClick={onReview}>Review All Controls</button> : <button onClick={onReview}>Check Readiness</button>}
        </div>
      </div>
    </section>
  );
}

function ControlDetails({ control, setControl, onReset }: { control: ControlInstance; setControl: (control: ControlInstance) => void; onReset: () => void }) {
  const baseFields = [['date', 'Date'], ['time', 'Time'], ['observer', 'Observer'], ['pcBranch', 'PC / Branch number'], ['employeeNumber', 'Employee number']];
  const customFields = control.definitionSnapshot.headerFields.map((field) => [field.id, field.label]);
  const fields = [...baseFields, ...customFields];
  return (
    <details className="details_box">
      <summary>Control details</summary>
      <div className="two_col">
        {fields.map(([key, label]) => <Field key={key} label={label}><input id={`${control.id}_${key}`} value={control.editableDetails[key] || ''} onChange={(event) => setControl({ ...control, editableDetails: { ...control.editableDetails, [key]: event.target.value } })} /></Field>)}
      </div>
      <button type="button" onClick={onReset}>Reset to session details</button>
    </details>
  );
}

function ChecklistRow({ controlId, response, photos, atPhotoMax, updateResponse, attachPhoto, removePhoto, busy }: { controlId: string; response: ItemResponse; photos: PhotoRecord[]; atPhotoMax: boolean; updateResponse: (controlId: string, itemId: string, patch: Partial<ItemResponse>) => void; attachPhoto: (file: File, controlId?: string, itemId?: string) => void; removePhoto: (photoId: string) => void; busy: boolean }) {
  const itemPhotos = photosForResponse(photos, response, controlId);
  const [commentOpen, setCommentOpen] = useState(Boolean(response.comment));
  const [photoOpen, setPhotoOpen] = useState(itemPhotos.length > 0);
  const showComment = response.state === 'exposed' || commentOpen || Boolean(response.comment);
  const showPhoto = response.state === 'exposed' || photoOpen || itemPhotos.length > 0;
  if (response.questionSnapshot.inactive) {
    return <article id={response.itemId} className="check_row inactive"><span className="item_number">{response.questionSnapshot.number}</span><p>{response.questionSnapshot.text || response.questionSnapshot.sourceNote}</p><small>{response.questionSnapshot.sourceNote}</small></article>;
  }
  return (
    <article id={response.itemId} className={`check_row ${response.state === 'exposed' ? 'exposed' : ''} ${response.flagged ? 'flagged' : ''}`}>
      <div className="item_number">{response.questionSnapshot.number}</div>
      <h3>{response.questionSnapshot.text}</h3>
      <div className="response_grid compact_response_grid" role="radiogroup" aria-label={`Response for item ${response.questionSnapshot.number}`}>
        {(['protected', 'exposed', 'na'] as ResponseState[]).map((state) => (
          <button
            type="button"
            key={state}
            className={response.state === state ? 'selected' : ''}
            onPointerDown={(event) => event.preventDefault()}
            onClick={() => updateResponse(controlId, response.itemId, { state })}
            aria-pressed={response.state === state}
          >
            {responseLabel(state)}
          </button>
        ))}
      </div>
      <div className="row_actions">
        <button type="button" className={showComment ? 'active' : ''} onClick={() => setCommentOpen((open) => !open)} aria-expanded={showComment}>Comment</button>
        <label className={`flag_toggle compact_flag ${response.flagged ? 'active' : ''}`}><input type="checkbox" checked={!!response.flagged} onChange={(event) => updateResponse(controlId, response.itemId, { flagged: event.target.checked })} />Flag</label>
        <button type="button" className={showPhoto ? 'active' : ''} onClick={() => setPhotoOpen((open) => !open)} aria-expanded={showPhoto}>Photo{itemPhotos.length ? ` ${itemPhotos.length}` : ''}</button>
      </div>
      <div className="row_expansion">
        {response.state === 'exposed' && <p className="required_hint">Exposed requires a comment. Photo is strongly recommended, but not required.</p>}
        {showComment && <Field label={response.state === 'exposed' ? 'Comment required for Exposed' : 'Comment'}><textarea value={response.comment || ''} onChange={(event) => updateResponse(controlId, response.itemId, { comment: event.target.value })} /></Field>}
        {showPhoto && (
          <>
            <PhotoTools disabled={atPhotoMax} busy={busy} onFile={(file) => attachPhoto(file, controlId, response.itemId)} maxMessage={atPhotoMax ? 'Maximum 3 photos per submission' : undefined} />
            <PhotoStrip photos={itemPhotos} removePhoto={removePhoto} compact />
          </>
        )}
      </div>
    </article>
  );
}

function ProactiveForm({ report, setReport, attachPhoto, removePhoto, onReview, issues, onGoToIssue, busy }: { report: ProactiveReport; setReport: (report: ProactiveReport) => void; attachPhoto: (file: File, label?: string) => void; removePhoto: (photoId: string) => void; onReview: () => void; issues: Issue[]; onGoToIssue: (issue: Issue) => void; busy: boolean }) {
  const set = (key: keyof ProactiveReport['fields'], value: string | string[]) => setReport({ ...report, fields: { ...report.fields, [key]: value }, updatedAt: new Date().toISOString() });
  const participants = report.fields.additionalParticipants || [];
  const [photoLabel, setPhotoLabel] = useState('');
  const setParticipant = (participantId: string, patch: Partial<Participant>) => {
    setReport({
      ...report,
      fields: {
        ...report.fields,
        additionalParticipants: participants.map((participant) => participant.id === participantId ? { ...participant, ...patch } : participant),
      },
      updatedAt: new Date().toISOString(),
    });
  };
  const addParticipant = () => {
    setReport({
      ...report,
      fields: {
        ...report.fields,
        additionalParticipants: [...participants, { id: id('participant'), name: '', employeeNumber: '' }],
      },
      updatedAt: new Date().toISOString(),
    });
  };
  const removeParticipant = (participantId: string) => {
    setReport({
      ...report,
      fields: {
        ...report.fields,
        additionalParticipants: participants.filter((participant) => participant.id !== participantId),
      },
      updatedAt: new Date().toISOString(),
    });
  };
  return (
    <section className="panel form_panel proactive_panel">
      <div className="section_header proactive_header">
        <div>
          <h1>{proactiveSource.title}</h1>
          <p>{proactiveSource.description}</p>
        </div>
        <PhotoCount photos={report.photos} />
      </div>
      <section className="proactive_section session_panel">
        <h2>Session Details</h2>
        <div className="two_col compact_fields">
          <Field label="Date" required><input id="date" type="date" value={report.fields.date} onChange={(e) => set('date', e.target.value)} /></Field>
          <Field label="Time" required><input id="time" type="time" value={report.fields.time} onChange={(e) => set('time', e.target.value)} /></Field>
        </div>
        <Field label="Name" required><input id="name" value={report.fields.name} onChange={(e) => set('name', e.target.value)} /></Field>
        <div className="two_col compact_fields">
          <Field label="PC / Branch" required><input id="pcNumber" value={report.fields.pcNumber} onChange={(e) => set('pcNumber', e.target.value)} /></Field>
          <Field label="Employee number"><input id="employeeNumber" value={report.fields.employeeNumber || ''} onChange={(e) => set('employeeNumber', e.target.value)} /></Field>
        </div>
        <div className="participants_box">
          <div className="section_header compact_header"><h3>Additional Participants</h3><button type="button" onClick={addParticipant}>Add participant</button></div>
          {participants.map((participant, index) => (
            <div className="participant_row" key={participant.id}>
              <Field label={`Participant ${index + 1} name`} required><input id={`participant_${participant.id}_name`} value={participant.name} onChange={(event) => setParticipant(participant.id, { name: event.target.value })} /></Field>
              <Field label="Employee number"><input value={participant.employeeNumber || ''} onChange={(event) => setParticipant(participant.id, { employeeNumber: event.target.value })} /></Field>
              <button type="button" onClick={() => removeParticipant(participant.id)}>Remove</button>
            </div>
          ))}
        </div>
      </section>
      <section className="proactive_section">
        <h2>Event Details</h2>
        <Field label="Location of Event" required><input id="location" value={report.fields.location} onChange={(e) => set('location', e.target.value)} /></Field>
        <OptionGroup idValue="eventType" label="Event" options={eventTypes} value={report.fields.eventType} setValue={(value) => set('eventType', value)} />
        {report.fields.eventType === 'Other' && <Field label="Other event" required><input id="eventOther" value={report.fields.eventOther} onChange={(e) => set('eventOther', e.target.value)} /></Field>}
      </section>
      <section id="eventLevel" className="reference_box">
        <h2>Event Level</h2>
        <p>{sifDefinition}</p>
        {eventLevels.map((level) => <label className="level_option" key={level.id}><input type="radio" name="eventLevel" checked={report.fields.eventLevel === level.id} onChange={() => set('eventLevel', level.id)} /><strong>{level.label}</strong><span>{level.description}</span></label>)}
      </section>
      <section className="proactive_section">
        <h2>Related Hazard</h2>
        <CheckboxGroup idValue="related" label="Near Hit or Good Catch Related to" options={relatedCategories} values={report.fields.related} setValues={(values) => set('related', values)} />
        {report.fields.related.includes('Other') && <Field label="Other related category" required><input id="relatedOther" value={report.fields.relatedOther} onChange={(e) => set('relatedOther', e.target.value)} /></Field>}
      </section>
      <section className="proactive_section">
        <h2>Observation and Corrective Action</h2>
        <Field label="Briefly describe your observation" required><textarea id="observation" value={report.fields.observation} onChange={(e) => set('observation', e.target.value)} /></Field>
        <Field label="What was done to correct the situation?" required><textarea id="correctiveAction" value={report.fields.correctiveAction} onChange={(e) => set('correctiveAction', e.target.value)} /></Field>
      </section>
      <section className="proactive_section">
        <div className="section_header compact_header"><h2>Supporting Photos</h2><span className="empty_note">Maximum 3 photos per report</span></div>
        <Field label="Photo label"><select value={photoLabel} onChange={(event) => setPhotoLabel(event.target.value)}><option value="">General</option><option value="Before">Before</option><option value="After">After</option></select></Field>
        <PhotoTools disabled={report.photos.length >= 3} busy={busy} onFile={(file) => attachPhoto(file, photoLabel || undefined)} maxMessage={report.photos.length >= 3 ? 'Maximum 3 photos per submission' : undefined} />
        <PhotoStrip photos={report.photos} removePhoto={removePhoto} />
      </section>
      {issues.length > 0 && (
        <div id="proactive_validation_near_action">
          <ValidationSummary issues={issues} onGo={onGoToIssue} />
        </div>
      )}
      <button className="primary_action compact sticky_action" onClick={onReview}>Review Proactive Report</button>
    </section>
  );
}

function CriticalReview({ session, setSession, onBack, onComplete, online }: { session: CriticalSession; setSession: (session: CriticalSession) => void; onBack: () => void; onComplete: (record: DraftRecord, mode: 'local' | 'export' | 'email') => void; online: boolean }) {
  const findings = session.controlInstances.flatMap((control) => activeResponses(control.responses).filter((response) => response.state === 'exposed' || response.flagged).map((response) => ({ control, response })));
  return (
    <section className="panel review_panel">
      <h1>Review Critical Controls</h1>
      <ReviewRecipients recipients={session.recipients} setRecipients={(recipients) => setSession({ ...session, recipients })} />
      <h2>Critical Findings</h2>
      {findings.length ? <FindingList findings={findings} photos={session.photos} /> : <p>No Exposed items or manual flags recorded.</p>}
      <SupportingPhotos controls={session.controlInstances} photos={session.photos} />
      <FullChecklistResults controls={session.controlInstances} photos={session.photos} />
      <div className="button_row"><button onClick={onBack}>Back</button><button onClick={() => onComplete(session, 'local')}>Complete Locally</button><button onClick={() => onComplete(session, 'export')}>Download Report</button><button className="primary_action compact" onClick={() => onComplete(session, 'email')}>{online ? 'Send Report' : 'Complete & Save for Sending'}</button></div>
    </section>
  );
}

function FindingList({ findings, photos }: { findings: { control: ControlInstance; response: ItemResponse }[]; photos: PhotoRecord[] }) {
  return (
    <div className="finding_list">
      {findings.map(({ control, response }) => {
        const attached = photosForResponse(photos, response, control.id);
        return (
          <div className="finding_card" key={`${control.id}_${response.itemId}`}>
            <div className="finding_title"><strong>{control.definitionSnapshot.title} item {response.questionSnapshot.number}</strong><StatusBadge state={response.state} /></div>
            {response.flagged && <span className="status_badge flag_badge">Manual Flag</span>}
            <p className="finding_question">{response.questionSnapshot.text}</p>
            <p><strong>Comment:</strong> {response.comment || 'No comment entered.'}</p>
            <PhotoStrip photos={attached} removePhoto={() => undefined} readonly compact />
          </div>
        );
      })}
    </div>
  );
}

function SupportingPhotos({ controls, photos }: { controls: ControlInstance[]; photos: PhotoRecord[] }) {
  const attached = controls.flatMap((control) =>
    activeResponses(control.responses).flatMap((response) =>
      photosForResponse(photos, response, control.id).map((photo) => ({ photo, control, response })),
    ),
  );
  const general = photos.filter((photo) => photo.associationType === 'general');
  return (
    <section className="supporting_section">
      <h2>Supporting Photos</h2>
      {!attached.length && !general.length ? (
        <p className="empty_note">No photos attached.</p>
      ) : (
        <div className="supporting_photo_list">
          {attached.map(({ photo, control, response }) => (
            <div className="supporting_photo_item" key={photo.id}>
              <strong>{control.definitionSnapshot.title} item {response.questionSnapshot.number}</strong>
              <p>{response.questionSnapshot.text}</p>
              <PhotoStrip photos={[photo]} removePhoto={() => undefined} readonly compact />
            </div>
          ))}
          {general.map((photo) => (
            <div className="supporting_photo_item" key={photo.id}>
              <strong>General evidence</strong>
              <PhotoStrip photos={[photo]} removePhoto={() => undefined} readonly compact />
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function FullChecklistResults({ controls, photos }: { controls: ControlInstance[]; photos: PhotoRecord[] }) {
  return (
    <details className="full_results" open>
      <summary>Full Checklist Results</summary>
      {controls.map((control) => (
        <div className="review_control" key={control.id}>
          <h3>{control.definitionSnapshot.title}</h3>
          <small>{control.definitionSnapshot.revision} | {control.definitionSnapshot.sourceDate}</small>
          {control.responses.map((response) => {
            const attached = photosForResponse(photos, response, control.id);
            return (
              <div className="review_item" key={response.itemId}>
                <div><strong>{response.questionSnapshot.number}.</strong> {response.questionSnapshot.text || response.questionSnapshot.sourceNote}</div>
                <div className="review_status_row">
                  <StatusBadge state={response.state} inactive={response.questionSnapshot.inactive} />
                  {response.flagged && <span className="status_badge flag_badge">Manual Flag</span>}
                  {response.comment && <span className="comment_preview">{response.comment}</span>}
                  {attached.length > 0 && <span className="comment_preview">{attached.length} photo{attached.length === 1 ? '' : 's'}</span>}
                </div>
              </div>
            );
          })}
        </div>
      ))}
    </details>
  );
}

function StatusBadge({ state, inactive }: { state?: ResponseState; inactive?: boolean }) {
  if (inactive) return <span className="status_badge inactive_badge">Inactive source row</span>;
  return <span className={`status_badge ${state || 'missing'}_badge`}>{responseLabel(state)}</span>;
}

const proactiveLevelFor = (report: ProactiveReport) =>
  (report.sourceSnapshot?.eventLevels || eventLevels).find((level) => level.id === report.fields.eventLevel);

function ProactiveSessionSummary({ report }: { report: ProactiveReport }) {
  const level = proactiveLevelFor(report);
  return (
    <>
      <dl className="meta_list">
        <div><dt>PC / Branch</dt><dd>{report.fields.pcNumber}</dd></div>
        <div><dt>Name</dt><dd>{report.fields.name}</dd></div>
        {report.fields.employeeNumber && <div><dt>Employee number</dt><dd>{report.fields.employeeNumber}</dd></div>}
        <div><dt>Date</dt><dd>{report.fields.date}</dd></div>
        <div><dt>Time</dt><dd>{report.fields.time}</dd></div>
        <div><dt>Location</dt><dd>{report.fields.location}</dd></div>
        <div><dt>Event</dt><dd>{report.fields.eventType}{report.fields.eventOther ? ` ${report.fields.eventOther}` : ''}</dd></div>
        <div><dt>Event Level</dt><dd>{level?.label || report.fields.eventLevel}</dd></div>
        <div><dt>Related</dt><dd>{report.fields.related.join(', ')}{report.fields.relatedOther ? ` ${report.fields.relatedOther}` : ''}</dd></div>
      </dl>
      {(report.fields.additionalParticipants || []).length > 0 && (
        <section className="review_subsection">
          <h2>Additional Participants</h2>
          <dl className="meta_list">
            {(report.fields.additionalParticipants || []).map((participant, index) => (
              <div key={participant.id}>
                <dt>Participant {index + 1}</dt>
                <dd>{participant.name}{participant.employeeNumber ? ` (${participant.employeeNumber})` : ''}</dd>
              </div>
            ))}
          </dl>
        </section>
      )}
      <section className="level_summary">
        <h2>Selected Event Level Definition</h2>
        {level ? <p><strong>{level.label}:</strong> {level.description}</p> : <p>No level definition found for this saved report.</p>}
      </section>
    </>
  );
}

function ProactiveReview({ report, setReport, onBack, onComplete, online }: { report: ProactiveReport; setReport: (report: ProactiveReport) => void; onBack: () => void; onComplete: (record: DraftRecord, mode: 'local' | 'export' | 'email') => void; online: boolean }) {
  return (
    <section className="panel review_panel">
      <h1>Review Proactive Report</h1>
      <ReviewRecipients recipients={report.recipients} setRecipients={(recipients) => setReport({ ...report, recipients })} />
      <ProactiveSessionSummary report={report} />
      <h2>Observation</h2><p>{report.fields.observation}</p>
      <h2>Corrective Action</h2><p>{report.fields.correctiveAction}</p>
      <h2>Supporting Photos</h2>
      <PhotoStrip photos={report.photos} removePhoto={() => undefined} readonly />
      <div className="button_row"><button onClick={onBack}>Back</button><button onClick={() => onComplete(report, 'local')}>Complete Locally</button><button onClick={() => onComplete(report, 'export')}>Download Report</button><button className="primary_action compact" onClick={() => onComplete(report, 'email')}>{online ? 'Send Report' : 'Complete & Save for Sending'}</button></div>
    </section>
  );
}

function History({ drafts, history, onHome, onResume, onView, onDelete, onPrepare }: { drafts: DraftRecord[]; history: DraftRecord[]; onHome: () => void; onResume: (record: DraftRecord) => void; onView: (record: DraftRecord) => void; onDelete: (record: DraftRecord, fromHistory?: boolean) => void; onPrepare: (record: DraftRecord, mode: 'local' | 'export' | 'email') => void }) {
  return (
    <section className="panel">
      <div className="section_header"><h1>History</h1><button onClick={onHome}>Home</button></div>
      <h2>Drafts</h2>
      <div className="record_list">{drafts.length ? drafts.map((record) => <RecordRow key={record.id} record={record} actionLabel="Resume" onAction={() => onResume(record)} secondary={<button onClick={() => onDelete(record)}>Delete</button>} />) : <p>No drafts.</p>}</div>
      <h2>Completed and Pending</h2>
      <div className="record_list">{history.length ? history.map((record) => <RecordRow key={record.id} record={record} actionLabel="View" onAction={() => onView(record)} secondary={<><button onClick={() => onPrepare(record, 'email')}>Email / Resend</button><button onClick={() => onPrepare(record, 'export')}>Download</button><button onClick={() => onDelete(record, true)}>Delete</button></>} />) : <p>No completed reports.</p>}</div>
    </section>
  );
}

function ReadOnlyRecord({ record, onBack, onPrepare, online }: { record: DraftRecord; onBack: () => void; onPrepare: (record: DraftRecord, mode: 'local' | 'export' | 'email') => void; online: boolean }) {
  if (record.type === 'proactive') {
    return (
      <section className="panel review_panel readonly_panel">
        <div className="section_header"><h1>Proactive Report</h1><button onClick={onBack}>Back to History</button></div>
        <ReadOnlyActions record={record} onPrepare={onPrepare} online={online} />
        <ProactiveSessionSummary report={record} />
        <h2>Observation</h2><p>{record.fields.observation}</p>
        <h2>Corrective Action</h2><p>{record.fields.correctiveAction}</p>
        <h2>Supporting Photos</h2>
        <PhotoStrip photos={record.photos} removePhoto={() => undefined} readonly compact />
      </section>
    );
  }
  const findings = record.controlInstances.flatMap((control) => activeResponses(control.responses).filter((response) => response.state === 'exposed' || response.flagged).map((response) => ({ control, response })));
  return (
    <section className="panel review_panel readonly_panel">
      <div className="section_header"><h1>Critical Control Report</h1><button onClick={onBack}>Back to History</button></div>
      <ReadOnlyActions record={record} onPrepare={onPrepare} online={online} />
      <dl className="meta_list"><div><dt>PC / Branch</dt><dd>{record.sharedDetails.pcBranch}</dd></div><div><dt>Observer</dt><dd>{record.sharedDetails.observer}</dd></div><div><dt>Date</dt><dd>{record.sharedDetails.date}</dd></div><div><dt>Time</dt><dd>{record.sharedDetails.time}</dd></div></dl>
      <h2>Critical Findings</h2>
      {findings.length ? <FindingList findings={findings} photos={record.photos} /> : <p>No Exposed items or manual flags recorded.</p>}
      <SupportingPhotos controls={record.controlInstances} photos={record.photos} />
      <FullChecklistResults controls={record.controlInstances} photos={record.photos} />
    </section>
  );
}

function ReadOnlyActions({ record, onPrepare, online }: { record: DraftRecord; onPrepare: (record: DraftRecord, mode: 'local' | 'export' | 'email') => void; online: boolean }) {
  return <div className="button_row readonly_actions"><button onClick={() => onPrepare(record, 'email')}>{online ? 'Email / Resend' : 'Mark Pending Send'}</button><button onClick={() => onPrepare(record, 'export')}>Download</button></div>;
}

function DeleteDialog({ target, onCancel, onConfirm }: { target: DeleteTarget; onCancel: () => void; onConfirm: () => void }) {
  const name = recordName(target.record);
  return (
    <div className="dialog_backdrop" role="presentation">
      <div className="dialog" role="dialog" aria-modal="true" aria-labelledby="delete_title">
        <h2 id="delete_title">Confirm Delete</h2>
        <p>This will permanently delete {name}. This action cannot be undone.</p>
        <div className="button_row"><button onClick={onCancel}>Cancel</button><button className="danger_button" onClick={onConfirm}>Confirm Delete</button></div>
      </div>
    </div>
  );
}

function ReviewRecipients({ recipients, setRecipients }: { recipients: string[]; setRecipients: (recipients: string[]) => void }) {
  const list = recipients.length ? recipients : [''];
  return <div className="recipient_box"><h2>Recipients</h2>{list.map((recipient, index) => <div className="recipient_row" key={index}><input type="email" value={recipient} onChange={(event) => { const next = [...list]; next[index] = event.target.value; setRecipients(next.map((item) => item.trim()).filter(Boolean)); }} aria-label={`Recipient ${index + 1}`} /><button onClick={() => setRecipients(list.filter((_, itemIndex) => itemIndex !== index))}>Remove</button></div>)}<button onClick={() => setRecipients([...list, ''])}>Add recipient</button></div>;
}

function ValidationSummary({ issues, onGo }: { issues: Issue[]; onGo: (issue: Issue) => void }) {
  return <section className="validation_panel" role="alert" aria-live="assertive"><h2>{issues.length} item{issues.length === 1 ? '' : 's'} still need attention</h2>{issues.map((issue, index) => <div className="validation_item" key={`${issue.key}_${index}`}><span><strong>{issue.label}</strong> {issue.message}</span><button onClick={() => onGo(issue)}>Go to item</button></div>)}</section>;
}

function Field({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return <label className="field_group"><span className="field_label">{label} {required && <b>Required</b>}</span>{children}</label>;
}

function OptionGroup({ idValue, label, options, value, setValue }: { idValue?: string; label: string; options: string[]; value: string; setValue: (value: string) => void }) {
  return <fieldset id={idValue} className="option_group"><legend>{label}</legend><div className="chip_grid">{options.map((option) => <button type="button" className={value === option ? 'selected' : ''} key={option} onClick={() => setValue(option)}>{option}</button>)}</div></fieldset>;
}

function CheckboxGroup({ idValue, label, options, values, setValues }: { idValue?: string; label: string; options: string[]; values: string[]; setValues: (values: string[]) => void }) {
  return <fieldset id={idValue} className="option_group"><legend>{label}</legend><div className="chip_grid">{options.map((option) => { const selected = values.includes(option); return <button type="button" className={selected ? 'selected' : ''} key={option} onClick={() => setValues(selected ? values.filter((item) => item !== option) : [...values, option])}>{option}</button>; })}</div></fieldset>;
}

function PhotoTools({ disabled, busy, onFile, maxMessage }: { disabled: boolean; busy: boolean; onFile: (file: File) => void; maxMessage?: string }) {
  const [inputSeed] = useState(() => id('photo_input'));
  const handle = (files: FileList | null) => {
    const file = files?.[0];
    if (file) onFile(file);
  };
  return <div className="photo_tools"><label className={disabled || busy ? 'disabled' : ''} htmlFor={`${inputSeed}_gallery`}>Add photo</label><input id={`${inputSeed}_gallery`} type="file" accept="image/*" disabled={disabled || busy} onChange={(event) => handle(event.target.files)} /><label className={disabled || busy ? 'disabled' : ''} htmlFor={`${inputSeed}_camera`}>Take photo</label><input id={`${inputSeed}_camera`} type="file" accept="image/*" capture="environment" disabled={disabled || busy} onChange={(event) => handle(event.target.files)} />{maxMessage && <small>{maxMessage}</small>}</div>;
}

function PhotoCount({ photos }: { photos: PhotoRecord[] }) {
  return <div className="photo_count"><strong>{photos.length} of 3 photos</strong><span>{photos.length >= 3 ? 'Maximum reached' : 'Maximum 3 photos per submission'}</span></div>;
}

function PhotoStrip({ photos, removePhoto, readonly, compact }: { photos: PhotoRecord[]; removePhoto: (photoId: string) => void; readonly?: boolean; compact?: boolean }) {
  if (!photos.length) return <p className="empty_note">No photos attached.</p>;
  return <div className={`photo_strip ${compact ? 'compact_photos' : ''}`}>{photos.map((photo) => <figure key={photo.id}><a href={photo.dataUrl} target="_blank" rel="noreferrer"><img src={photo.dataUrl} alt={photo.label || photo.name || 'Attached photo'} /></a><figcaption>{photo.label || photo.name || 'Photo'}</figcaption>{!readonly && <button onClick={() => removePhoto(photo.id)}>Remove</button>}</figure>)}</div>;
}

function RecordRow({ record, actionLabel, onAction, secondary }: { record: DraftRecord; actionLabel: string; onAction: () => void; secondary?: React.ReactNode }) {
  const branch = record.type === 'critical' ? record.sharedDetails.pcBranch : record.fields.pcNumber;
  return <div className="record_row"><div><strong>{recordName(record)}</strong><span>{record.status.replaceAll('_', ' ')} | {new Date(record.updatedAt).toLocaleString()}</span><small>PC / Branch: {branch || 'Not provided'} | {photoCountLabel(photoTotal(record))}</small></div><div className="record_actions"><button onClick={onAction}>{actionLabel}</button>{secondary}</div></div>;
}

function recordName(record: DraftRecord) {
  return record.type === 'critical' ? record.controlInstances.map((control) => control.definitionSnapshot.title).join(', ') || 'Critical Controls' : 'Proactive Report';
}
